/* eslint-disable no-unused-vars */
// Pure JS calculator engine — mirrors backend calculators.py for instant client-side feedback.
// Backend is source of truth for saved/shared computations; this is for live UI.

function round(v, d = 2) {
    return Number((Number(v) || 0).toFixed(d));
}

export function calcSIP({ monthly, rate, years }) {
    monthly = Number(monthly) || 0;
    rate = Number(rate) || 0;
    years = Number(years) || 0;
    const n = Math.round(years * 12);
    const r = rate / 100 / 12;
    const fv = r > 0 ? monthly * (((1 + r) ** n - 1) / r) * (1 + r) : monthly * n;
    const invested = monthly * n;
    const series = [];
    let balance = 0;
    for (let m = 1; m <= n; m++) {
        balance = balance * (1 + r) + monthly;
        if (m % 12 === 0 || m === n) series.push({ year: m / 12, invested: round(monthly * m), value: round(balance) });
    }
    return { future_value: round(fv), invested: round(invested), gains: round(fv - invested), series };
}

export function calcStepupSIP({ monthly, rate, years, stepUp }) {
    monthly = Number(monthly) || 0;
    rate = Number(rate) || 0;
    years = Math.round(Number(years) || 0);
    const step = (Number(stepUp) || 0) / 100;
    const r = rate / 100 / 12;
    let balance = 0;
    let invested = 0;
    let current = monthly;
    const series = [];
    for (let y = 1; y <= years; y++) {
        for (let m = 0; m < 12; m++) {
            balance = balance * (1 + r) + current;
            invested += current;
        }
        series.push({ year: y, invested: round(invested), value: round(balance) });
        current *= 1 + step;
    }
    return { future_value: round(balance), invested: round(invested), gains: round(balance - invested), series };
}

export function calcLumpsum({ principal, rate, years }) {
    principal = Number(principal) || 0;
    rate = Number(rate) || 0;
    years = Number(years) || 0;
    const fv = principal * (1 + rate / 100) ** years;
    const series = [];
    for (let y = 0; y <= Math.floor(years); y++) series.push({ year: y, value: round(principal * (1 + rate / 100) ** y) });
    return { future_value: round(fv), invested: round(principal), gains: round(fv - principal), series };
}

export function calcEMI({ principal, rate, years }) {
    principal = Number(principal) || 0;
    rate = Number(rate) || 0;
    years = Number(years) || 0;
    const n = Math.round(years * 12);
    const r = rate / 100 / 12;
    const emi = r > 0 ? (principal * r * (1 + r) ** n) / ((1 + r) ** n - 1) : principal / n;
    const total = emi * n;
    const interest = total - principal;
    let balance = principal;
    const schedule = [];
    let yearlyInterest = 0;
    let yearlyPrincipal = 0;
    for (let m = 1; m <= n; m++) {
        const ip = balance * r;
        const pp = emi - ip;
        balance -= pp;
        yearlyInterest += ip;
        yearlyPrincipal += pp;
        if (m % 12 === 0 || m === n) {
            schedule.push({
                year: Math.ceil(m / 12),
                principal: round(yearlyPrincipal),
                interest: round(yearlyInterest),
                balance: round(Math.max(balance, 0)),
            });
            yearlyInterest = 0;
            yearlyPrincipal = 0;
        }
    }
    return { emi: round(emi), total_payment: round(total), total_interest: round(interest), principal: round(principal), schedule };
}

export function calcFD({ principal, rate, years, frequency = 4 }) {
    principal = Number(principal) || 0;
    rate = Number(rate) || 0;
    years = Number(years) || 0;
    const maturity = principal * (1 + rate / 100 / frequency) ** (frequency * years);
    const series = [];
    for (let y = 0; y <= Math.floor(years); y++) series.push({ year: y, value: round(principal * (1 + rate / 100 / frequency) ** (frequency * y)) });
    return { maturity_value: round(maturity), principal: round(principal), interest_earned: round(maturity - principal), series };
}

export function calcRD({ monthly, rate, years }) {
    monthly = Number(monthly) || 0;
    rate = Number(rate) || 0;
    years = Number(years) || 0;
    const n = Math.round(years * 12);
    const r = rate / 100 / 12;
    let balance = 0;
    for (let m = 0; m < n; m++) balance = balance * (1 + r) + monthly;
    const invested = monthly * n;
    return { maturity_value: round(balance), invested: round(invested), interest_earned: round(balance - invested) };
}

export function calcCAGR({ initial, final, years }) {
    initial = Number(initial) || 0;
    final = Number(final) || 0;
    years = Number(years) || 0;
    if (initial <= 0 || years <= 0) return { cagr_percent: 0, absolute_return: 0, absolute_return_percent: 0 };
    const cagr = ((final / initial) ** (1 / years) - 1) * 100;
    const abs = final - initial;
    return { cagr_percent: round(cagr), absolute_return: round(abs), absolute_return_percent: round((abs / initial) * 100) };
}

export function calcSWP({ corpus, monthly, rate, years }) {
    corpus = Number(corpus) || 0;
    monthly = Number(monthly) || 0;
    rate = Number(rate) || 0;
    years = Number(years) || 0;
    const n = Math.round(years * 12);
    const r = rate / 100 / 12;
    let balance = corpus;
    let totalWithdrawn = 0;
    const series = [];
    for (let m = 1; m <= n; m++) {
        balance = balance * (1 + r) - monthly;
        totalWithdrawn += monthly;
        if (m % 12 === 0 || m === n) series.push({ year: Math.ceil(m / 12), balance: round(Math.max(balance, 0)) });
        if (balance <= 0) break;
    }
    return { final_balance: round(Math.max(balance, 0)), total_withdrawn: round(totalWithdrawn), corpus: round(corpus), series };
}

export function calcPPF({ yearly, rate = 7.1, years = 15 }) {
    yearly = Number(yearly) || 0;
    let balance = 0;
    let invested = 0;
    const series = [];
    for (let y = 1; y <= years; y++) {
        balance += yearly;
        invested += yearly;
        balance *= 1 + rate / 100;
        series.push({ year: y, invested: round(invested), value: round(balance) });
    }
    return { maturity_value: round(balance), invested: round(invested), interest_earned: round(balance - invested), series };
}

export function calcInflation({ current, rate, years }) {
    current = Number(current) || 0;
    rate = Number(rate) || 0;
    years = Number(years) || 0;
    const future = current * (1 + rate / 100) ** years;
    const power = current / (1 + rate / 100) ** years;
    return { future_cost: round(future), current_amount: round(current), purchasing_power: round(power), loss_in_value: round(future - current) };
}

export function calcRetirement({ currentAge, retireAge, lifeExp, monthlyExpense, inflation, preReturn, postReturn }) {
    currentAge = Number(currentAge) || 0;
    retireAge = Number(retireAge) || 0;
    lifeExp = Number(lifeExp) || 0;
    monthlyExpense = Number(monthlyExpense) || 0;
    inflation = Number(inflation) || 0;
    preReturn = Number(preReturn) || 0;
    postReturn = Number(postReturn) || 0;
    const ytr = retireAge - currentAge;
    const ry = lifeExp - retireAge;
    const futureMonthly = monthlyExpense * (1 + inflation / 100) ** ytr;
    const realReturn = (1 + postReturn / 100) / (1 + inflation / 100) - 1;
    let corpus;
    if (realReturn <= 0) corpus = futureMonthly * 12 * ry;
    else {
        const rm = realReturn / 12;
        const n = ry * 12;
        corpus = futureMonthly * ((1 - (1 + rm) ** -n) / rm);
    }
    const nInv = ytr * 12;
    const rInv = preReturn / 100 / 12;
    const sip = rInv > 0 && nInv > 0 ? corpus / ((((1 + rInv) ** nInv - 1) / rInv) * (1 + rInv)) : 0;
    return {
        corpus_required: round(corpus),
        monthly_sip: round(sip),
        future_monthly_expense: round(futureMonthly),
        years_to_retire: ytr,
    };
}

export function calcGST({ amount, rate, inclusive = false }) {
    amount = Number(amount) || 0;
    rate = Number(rate) || 0;
    let base, gst, total;
    if (inclusive) {
        base = amount / (1 + rate / 100);
        gst = amount - base;
        total = amount;
    } else {
        base = amount;
        gst = (amount * rate) / 100;
        total = amount + gst;
    }
    return { base_amount: round(base), gst_amount: round(gst), total_amount: round(total), cgst: round(gst / 2), sgst: round(gst / 2) };
}

export function calcSI({ principal, rate, years }) {
    principal = Number(principal) || 0;
    rate = Number(rate) || 0;
    years = Number(years) || 0;
    const interest = (principal * rate * years) / 100;
    return { interest: round(interest), total_amount: round(principal + interest), principal: round(principal) };
}

export function calcCI({ principal, rate, years, frequency = 1 }) {
    principal = Number(principal) || 0;
    rate = Number(rate) || 0;
    years = Number(years) || 0;
    const amt = principal * (1 + rate / 100 / frequency) ** (frequency * years);
    const series = [];
    for (let y = 0; y <= Math.floor(years); y++) series.push({ year: y, value: round(principal * (1 + rate / 100 / frequency) ** (frequency * y)) });
    return { total_amount: round(amt), interest: round(amt - principal), principal: round(principal), series };
}

export function calcGoal({ target, years, rate, currentSavings = 0 }) {
    target = Number(target) || 0;
    years = Number(years) || 0;
    rate = Number(rate) || 0;
    currentSavings = Number(currentSavings) || 0;
    const n = Math.round(years * 12);
    const r = rate / 100 / 12;
    const fvCurrent = currentSavings * (1 + rate / 100) ** years;
    const need = Math.max(target - fvCurrent, 0);
    const sip = r > 0 && n > 0 ? need / ((((1 + r) ** n - 1) / r) * (1 + r)) : need / Math.max(n, 1);
    return {
        monthly_sip_required: round(sip),
        target_amount: round(target),
        fv_current_savings: round(fvCurrent),
        additional_needed: round(need),
    };
}

export function calcFIRE({ annualExpense, currentSavings, monthlySavings, rate, withdrawalRate = 4 }) {
    annualExpense = Number(annualExpense) || 0;
    currentSavings = Number(currentSavings) || 0;
    monthlySavings = Number(monthlySavings) || 0;
    rate = Number(rate) || 0;
    const fireCorpus = annualExpense * (100 / withdrawalRate);
    let balance = currentSavings;
    let years = 0;
    const series = [{ year: 0, value: round(balance) }];
    while (balance < fireCorpus && years < 80) {
        years++;
        for (let m = 0; m < 12; m++) balance = balance * (1 + rate / 100 / 12) + monthlySavings;
        series.push({ year: years, value: round(balance) });
    }
    return { fire_corpus: round(fireCorpus), years_to_fire: years, final_balance: round(balance), series };
}

export function calcRealEstateROI({ purchase, sale, years, rental = 0, expenses = 0 }) {
    purchase = Number(purchase) || 0;
    sale = Number(sale) || 0;
    years = Number(years) || 0;
    const totalReturn = sale - purchase + rental * years - expenses;
    const roi = purchase > 0 ? (totalReturn / purchase) * 100 : 0;
    const cagr = purchase > 0 && years > 0 ? ((sale / purchase) ** (1 / years) - 1) * 100 : 0;
    return { total_return: round(totalReturn), roi_percent: round(roi), capital_cagr_percent: round(cagr) };
}

export function calcLoanEligibility({ income, existingEMI = 0, rate, tenure, foir = 50 }) {
    income = Number(income) || 0;
    existingEMI = Number(existingEMI) || 0;
    rate = Number(rate) || 0;
    tenure = Number(tenure) || 0;
    const maxEMI = income * (foir / 100) - existingEMI;
    if (maxEMI <= 0) return { eligible_loan: 0, max_emi: 0 };
    const n = Math.round(tenure * 12);
    const r = rate / 100 / 12;
    const loan = r > 0 ? (maxEMI * ((1 + r) ** n - 1)) / (r * (1 + r) ** n) : maxEMI * n;
    return { eligible_loan: round(loan), max_emi: round(maxEMI) };
}

export function calcDebtPayoff({ debt, rate, monthlyPayment }) {
    debt = Number(debt) || 0;
    rate = Number(rate) || 0;
    monthlyPayment = Number(monthlyPayment) || 0;
    let balance = debt;
    let months = 0;
    let totalInterest = 0;
    const r = rate / 100 / 12;
    while (balance > 0 && months < 600) {
        const interest = balance * r;
        balance = balance + interest - monthlyPayment;
        totalInterest += interest;
        months++;
        if (monthlyPayment <= interest) return { months_to_payoff: -1, total_interest: -1, note: "Payment too low — debt grows forever." };
    }
    return {
        months_to_payoff: months,
        years_to_payoff: round(months / 12, 1),
        total_interest: round(totalInterest),
        total_paid: round(debt + totalInterest),
    };
}

export function calcAssetAllocation({ age, risk = "moderate" }) {
    age = Number(age) || 30;
    let equity = Math.max(Math.min(100 - age, 80), 20);
    if (risk === "conservative") equity = Math.max(equity - 15, 20);
    if (risk === "aggressive") equity = Math.min(equity + 15, 90);
    const gold = 10;
    const debt = Math.max(100 - equity - gold, 0);
    return {
        equity_percent: equity,
        debt_percent: debt,
        gold_percent: gold,
        allocation: [
            { name: "Equity", value: equity },
            { name: "Debt", value: debt },
            { name: "Gold", value: gold },
        ],
    };
}

// Registry with UI metadata
export const CALCULATORS = {
    sip: {
        slug: "sip",
        name: "SIP Calculator",
        category: "Investments",
        icon: "TrendingUp",
        tagline: "Project the future value of your monthly SIP.",
        about: "Systematic Investment Plan lets you invest a fixed amount monthly into mutual funds. Compounding does the heavy lifting over the years.",
        formula: "FV = P × [((1 + r)^n - 1) / r] × (1 + r)",
        formulaExplanation: [
            "FV = Future Value",
            "P = Monthly SIP amount",
            "r = Monthly interest rate (annual rate / 12)",
            "n = Total number of months",
            "Uses monthly compounding",
            "Assumes SIP invested at end of each month",

        ],
        defaults: { monthly: 10000, rate: 12, years: 10 },
        fields: [
            { key: "monthly", label: "Monthly Investment", unit: "₹", min: 500, max: 200000, step: 500 },
            { key: "rate", label: "Expected Return (p.a.)", unit: "%", min: 1, max: 30, step: 0.5 },
            { key: "years", label: "Time Period", unit: "yrs", min: 1, max: 40, step: 1 },
        ],
        run: calcSIP,
        chartType: "line",
    },
    "stepup-sip": {
        slug: "stepup-sip",
        name: "Step-up SIP",
        category: "Investments",
        icon: "Stairs",
        tagline: "Increase your SIP every year and supercharge growth.",
        about: "A step-up SIP grows your monthly amount by a fixed % every year — matching your income trajectory.",
        formula:"FV = Σ [P × (1 + s)^y × (1 + r)^n]",

        formulaExplanation: [
            "P = Initial monthly SIP",
            "s = Annual step-up percentage",
            "r = Monthly interest rate",
            "y = Current year index",
            "n = Remaining months",
            "Monthly SIP increases every year",
            "Uses nominal annual return divided by 12",
        ],
        defaults: { monthly: 10000, rate: 12, years: 15, stepUp: 10 },
        fields: [
            { key: "monthly", label: "Starting Monthly SIP", unit: "₹", min: 500, max: 200000, step: 500 },
            { key: "rate", label: "Expected Return (p.a.)", unit: "%", min: 1, max: 30, step: 0.5 },
            { key: "years", label: "Time Period", unit: "yrs", min: 1, max: 40, step: 1 },
            { key: "stepUp", label: "Annual Step-up", unit: "%", min: 0, max: 50, step: 1 },
        ],
        run: calcStepupSIP,
        chartType: "line",
    },
    lumpsum: {
        slug: "lumpsum",
        name: "Lumpsum Calculator",
        category: "Investments",
        icon: "Coins",
        tagline: "How a one-time investment grows over time.",
        about: "Project the future value of a single lumpsum amount at a chosen annual return.",
        formula: "FV = P × (1 + r)^n",

        formulaExplanation: [
            "FV = Future Value",
            "P = Principal investment",
            "r = Annual return rate",
            "n = Number of years",
            "Uses annual compounding",
        ],
        defaults: { principal: 100000, rate: 12, years: 10 },
        fields: [
            { key: "principal", label: "Investment Amount", unit: "₹", min: 1000, max: 10000000, step: 1000 },
            { key: "rate", label: "Expected Return (p.a.)", unit: "%", min: 1, max: 30, step: 0.5 },
            { key: "years", label: "Time Period", unit: "yrs", min: 1, max: 40, step: 1 },
        ],
        run: calcLumpsum,
        chartType: "line",
    },
    emi: {
        slug: "emi",
        name: "EMI Calculator",
        category: "Loans",
        icon: "Receipt",
        tagline: "Equated monthly instalment on any loan.",
        about: "Calculate your monthly EMI, total interest, and the principal–interest split for any loan.",
        formula: "EMI = [P × r × (1 + r)^n] / [(1 + r)^n − 1]",

        formulaExplanation: [
            "P = Loan principal",
            "r = Monthly interest rate",
            "n = Total number of monthly instalments",
            "Uses reducing balance method",
            "Interest calculated monthly",
        ],
        defaults: { principal: 1000000, rate: 9, years: 20 },
        fields: [
            { key: "principal", label: "Loan Amount", unit: "₹", min: 10000, max: 100000000, step: 10000 },
            { key: "rate", label: "Interest Rate (p.a.)", unit: "%", min: 1, max: 30, step: 0.25 },
            { key: "years", label: "Loan Tenure", unit: "yrs", min: 1, max: 30, step: 1 },
        ],
        run: calcEMI,
        chartType: "emi",
    },
    fd: {
        slug: "fd",
        name: "FD Calculator",
        category: "Savings",
        icon: "PiggyBank",
        tagline: "Project your fixed deposit maturity value.",
        about: "Compound-interest projection for fixed deposits with configurable compounding frequency.",
        formula: "A = P × (1 + r / n)^(n × t)",

        formulaExplanation: [
            "A = Maturity amount",
            "P = Principal deposit",
            "r = Annual interest rate",
            "n = Compounding frequency",
            "t = Time in years",
        ],
        defaults: { principal: 100000, rate: 7, years: 5 },
        fields: [
            { key: "principal", label: "Deposit Amount", unit: "₹", min: 1000, max: 10000000, step: 1000 },
            { key: "rate", label: "Interest Rate (p.a.)", unit: "%", min: 1, max: 15, step: 0.25 },
            { key: "years", label: "Tenure", unit: "yrs", min: 1, max: 20, step: 1 },
        ],
        run: calcFD,
        chartType: "line",
    },
    rd: {
        slug: "rd",
        name: "RD Calculator",
        category: "Savings",
        icon: "Repeat",
        tagline: "Recurring deposit maturity & returns.",
        about: "Monthly recurring deposit projection with quarterly compounding.",
        formula: "Recurring monthly deposits compounded monthly",

        formulaExplanation: [
            "Monthly deposits added every month",
            "Interest compounded monthly",
            "Uses reducing accumulation model",
        ],
        defaults: { monthly: 5000, rate: 6.5, years: 5 },
        fields: [
            { key: "monthly", label: "Monthly Deposit", unit: "₹", min: 100, max: 100000, step: 100 },
            { key: "rate", label: "Interest Rate (p.a.)", unit: "%", min: 1, max: 12, step: 0.25 },
            { key: "years", label: "Tenure", unit: "yrs", min: 1, max: 10, step: 1 },
        ],
        run: calcRD,
        chartType: "none",
    },
    cagr: {
        slug: "cagr",
        name: "CAGR Calculator",
        category: "Investments",
        icon: "LineChart",
        tagline: "Compound annual growth rate of any investment.",
        about: "Find out how much your investment has grown — annualised.",
        formula: "CAGR = [(FV / IV)^(1 / n)] − 1",

        formulaExplanation: [
            "FV = Final value",
            "IV = Initial value",
            "n = Number of years",
            "Shows annualized growth rate",
        ],
        defaults: { initial: 100000, final: 200000, years: 5 },
        fields: [
            { key: "initial", label: "Initial Value", unit: "₹", min: 100, max: 100000000, step: 1000 },
            { key: "final", label: "Final Value", unit: "₹", min: 100, max: 100000000, step: 1000 },
            { key: "years", label: "Duration", unit: "yrs", min: 1, max: 50, step: 1 },
        ],
        run: calcCAGR,
        chartType: "none",
    },
    swp: {
        slug: "swp",
        name: "SWP Calculator",
        category: "Investments",
        icon: "ArrowDownLeft",
        tagline: "Systematic withdrawal plan for retirees.",
        about: "Project how long your corpus will last while you withdraw a fixed amount monthly.",
        formula: "Monthly withdrawals with compounding balance",

        formulaExplanation: [
            "Corpus compounds monthly",
            "Fixed withdrawal deducted monthly",
            "Projection assumes constant return rate",
        ],
        defaults: { corpus: 1000000, monthly: 10000, rate: 8, years: 15 },
        fields: [
            { key: "corpus", label: "Corpus", unit: "₹", min: 10000, max: 100000000, step: 10000 },
            { key: "monthly", label: "Monthly Withdrawal", unit: "₹", min: 1000, max: 1000000, step: 1000 },
            { key: "rate", label: "Expected Return (p.a.)", unit: "%", min: 1, max: 20, step: 0.5 },
            { key: "years", label: "Years", unit: "yrs", min: 1, max: 40, step: 1 },
        ],
        run: calcSWP,
        chartType: "balance",
    },
    ppf: {
        slug: "ppf",
        name: "PPF Calculator",
        category: "Savings",
        icon: "Shield",
        tagline: "Public Provident Fund maturity over 15 years.",
        about: "PPF is a tax-free long-term savings option. Default rate ~7.1%.",
        formula: "Yearly deposits compounded annually",

        formulaExplanation: [
            "Uses annual compounding",
            "Interest rate configurable",
            "PPF rates are government controlled",
        ],
        defaults: { yearly: 150000, rate: 7.1, years: 15 },
        fields: [
            { key: "yearly", label: "Yearly Deposit", unit: "₹", min: 500, max: 150000, step: 500 },
            { key: "rate", label: "Interest Rate (p.a.)", unit: "%", min: 1, max: 12, step: 0.1 },
            { key: "years", label: "Tenure", unit: "yrs", min: 1, max: 50, step: 1 },
        ],
        run: calcPPF,
        chartType: "line",
    },
    inflation: {
        slug: "inflation",
        name: "Inflation Calculator",
        category: "Planning",
        icon: "Flame",
        tagline: "Future cost of today's money.",
        about: "See how inflation will erode purchasing power and what an amount will be worth.",
        formula: "FV = PV × (1 + i)^n",

        formulaExplanation: [
            "FV = Future value",
            "PV = Present value",
            "i = Inflation rate",
            "n = Number of years",
        ],
        defaults: { current: 100000, rate: 6, years: 10 },
        fields: [
            { key: "current", label: "Current Amount", unit: "₹", min: 100, max: 100000000, step: 1000 },
            { key: "rate", label: "Inflation Rate (p.a.)", unit: "%", min: 0, max: 20, step: 0.5 },
            { key: "years", label: "Years", unit: "yrs", min: 1, max: 50, step: 1 },
        ],
        run: calcInflation,
        chartType: "none",
    },
    retirement: {
        slug: "retirement",
        name: "Retirement Calculator",
        category: "Planning",
        icon: "Sunset",
        tagline: "Corpus & SIP required for a comfortable retirement.",
        about: "Plug in your current age and lifestyle to see what corpus you need and how much to save monthly.",
        formula: "Retirement corpus projection using inflation-adjusted withdrawal model",

        formulaExplanation: [
            "Expenses adjusted for inflation",
            "Corpus estimated using post-retirement return",
            "Monthly SIP calculated using pre-retirement return",
            "Results are indicative projections",
        ],
        defaults: { currentAge: 30, retireAge: 60, lifeExp: 85, monthlyExpense: 50000, inflation: 6, preReturn: 12, postReturn: 7 },
        fields: [
            { key: "currentAge", label: "Current Age", unit: "yrs", min: 18, max: 60, step: 1 },
            { key: "retireAge", label: "Retirement Age", unit: "yrs", min: 40, max: 70, step: 1 },
            { key: "lifeExp", label: "Life Expectancy", unit: "yrs", min: 60, max: 100, step: 1 },
            { key: "monthlyExpense", label: "Current Monthly Expense", unit: "₹", min: 5000, max: 1000000, step: 1000 },
            { key: "inflation", label: "Inflation", unit: "%", min: 0, max: 15, step: 0.5 },
            { key: "preReturn", label: "Pre-retirement Return", unit: "%", min: 1, max: 25, step: 0.5 },
            { key: "postReturn", label: "Post-retirement Return", unit: "%", min: 1, max: 15, step: 0.5 },
        ],
        run: calcRetirement,
        chartType: "none",
    },
    gst: {
        slug: "gst",
        name: "GST Calculator",
        category: "Tax",
        icon: "Calculator",
        tagline: "GST inclusive and exclusive computation.",
        about: "Find CGST/SGST split and net/total amounts for any GST rate.",
        formula: "GST = Amount × GST Rate / 100",

        formulaExplanation: [
            "Supports inclusive and exclusive GST",
            "GST split equally into CGST and SGST",
        ],
        defaults: { amount: 1000, rate: 18, inclusive: false },
        fields: [
            { key: "amount", label: "Amount", unit: "₹", min: 1, max: 10000000, step: 1 },
            { key: "rate", label: "GST Rate", unit: "%", min: 0, max: 28, step: 1 },
        ],
        toggle: { key: "inclusive", label: "Amount is GST-inclusive" },
        run: calcGST,
        chartType: "none",
    },
    "simple-interest": {
        slug: "simple-interest",
        name: "Simple Interest",
        category: "Basics",
        icon: "Percent",
        tagline: "Quick SI calculation.",
        about: "Simple Interest = P × R × T / 100.",
        formula: "SI = (P × R × T) / 100",

        formulaExplanation: [
            "P = Principal",
            "R = Interest rate",
            "T = Time in years",
        ],
        defaults: { principal: 100000, rate: 8, years: 5 },
        fields: [
            { key: "principal", label: "Principal", unit: "₹", min: 100, max: 10000000, step: 1000 },
            { key: "rate", label: "Rate (p.a.)", unit: "%", min: 0, max: 30, step: 0.25 },
            { key: "years", label: "Years", unit: "yrs", min: 1, max: 30, step: 1 },
        ],
        run: calcSI,
        chartType: "none",
    },
    "compound-interest": {
        slug: "compound-interest",
        name: "Compound Interest",
        category: "Basics",
        icon: "Percent",
        tagline: "The 8th wonder of the world.",
        about: "Compound interest with annual compounding by default.",
        formula: "A = P × (1 + r / n)^(n × t)",

        formulaExplanation: [
            "P = Principal",
            "r = Annual rate",
            "n = Compounding frequency",
            "t = Time in years",
        ],
        defaults: { principal: 100000, rate: 8, years: 10 },
        fields: [
            { key: "principal", label: "Principal", unit: "₹", min: 100, max: 10000000, step: 1000 },
            { key: "rate", label: "Rate (p.a.)", unit: "%", min: 0, max: 30, step: 0.25 },
            { key: "years", label: "Years", unit: "yrs", min: 1, max: 50, step: 1 },
        ],
        run: calcCI,
        chartType: "line",
    },
    "home-loan": {
        slug: "home-loan",
        name: "Home Loan Calculator",
        category: "Loans",
        icon: "Home",
        tagline: "EMI, total interest & amortisation for home loans.",
        about: "Standard home loan EMI calculator with yearly amortisation.",
        defaults: { principal: 5000000, rate: 8.5, years: 25 },
        fields: [
            { key: "principal", label: "Loan Amount", unit: "₹", min: 100000, max: 100000000, step: 100000 },
            { key: "rate", label: "Interest Rate", unit: "%", min: 5, max: 15, step: 0.25 },
            { key: "years", label: "Tenure", unit: "yrs", min: 1, max: 30, step: 1 },
        ],
        run: calcEMI,
        chartType: "emi",
    },
    "car-loan": {
        slug: "car-loan",
        name: "Car Loan Calculator",
        category: "Loans",
        icon: "Car",
        tagline: "Auto loan EMI breakup.",
        about: "Plan car loan EMIs and total interest.",
        defaults: { principal: 800000, rate: 9.5, years: 5 },
        fields: [
            { key: "principal", label: "Loan Amount", unit: "₹", min: 50000, max: 10000000, step: 10000 },
            { key: "rate", label: "Interest Rate", unit: "%", min: 5, max: 15, step: 0.25 },
            { key: "years", label: "Tenure", unit: "yrs", min: 1, max: 8, step: 1 },
        ],
        run: calcEMI,
        chartType: "emi",
    },
    "personal-loan": {
        slug: "personal-loan",
        name: "Personal Loan Calculator",
        category: "Loans",
        icon: "Wallet",
        tagline: "Personal loan EMI & cost of borrowing.",
        about: "Personal loans have higher rates — see the true cost.",
        defaults: { principal: 300000, rate: 13, years: 3 },
        fields: [
            { key: "principal", label: "Loan Amount", unit: "₹", min: 10000, max: 5000000, step: 10000 },
            { key: "rate", label: "Interest Rate", unit: "%", min: 8, max: 30, step: 0.25 },
            { key: "years", label: "Tenure", unit: "yrs", min: 1, max: 7, step: 1 },
        ],
        run: calcEMI,
        chartType: "emi",
    },
    "education-loan": {
        slug: "education-loan",
        name: "Education Loan Calculator",
        category: "Loans",
        icon: "GraduationCap",
        tagline: "Student loan EMI planner.",
        about: "Education loan EMI with moratorium-aware planning.",
        defaults: { principal: 1500000, rate: 10.5, years: 10 },
        fields: [
            { key: "principal", label: "Loan Amount", unit: "₹", min: 50000, max: 10000000, step: 50000 },
            { key: "rate", label: "Interest Rate", unit: "%", min: 6, max: 16, step: 0.25 },
            { key: "years", label: "Tenure", unit: "yrs", min: 1, max: 15, step: 1 },
        ],
        run: calcEMI,
        chartType: "emi",
    },
    "goal-planning": {
        slug: "goal-planning",
        name: "Goal Planning",
        category: "Planning",
        icon: "Target",
        tagline: "Save monthly to hit a future goal.",
        about: "Tell us your target amount, time horizon and expected return — we'll tell you the monthly SIP.",
        defaults: { target: 1000000, years: 10, rate: 12, currentSavings: 0 },
        fields: [
            { key: "target", label: "Target Amount", unit: "₹", min: 10000, max: 100000000, step: 10000 },
            { key: "years", label: "Years to Goal", unit: "yrs", min: 1, max: 40, step: 1 },
            { key: "rate", label: "Expected Return", unit: "%", min: 1, max: 25, step: 0.5 },
            { key: "currentSavings", label: "Current Savings", unit: "₹", min: 0, max: 100000000, step: 10000 },
        ],
        run: calcGoal,
        chartType: "none",
    },
    "net-worth": {
        slug: "net-worth",
        name: "Net Worth Calculator",
        category: "Planning",
        icon: "Layers",
        tagline: "Assets minus liabilities — your true wealth.",
        about: "Add your major assets and liabilities to compute your current net worth.",
        formula: "Net Worth = Assets − Liabilities",

        formulaExplanation: [
            "Assets include investments and property",
            "Liabilities include loans and obligations",
        ],
        defaults: { assets: 0, liabilities: 0 },
        fields: [
            { key: "assets", label: "Total Assets", unit: "₹", min: 0, max: 1000000000, step: 1000 },
            { key: "liabilities", label: "Total Liabilities", unit: "₹", min: 0, max: 1000000000, step: 1000 },
        ],
        run: (i) => ({ net_worth: round((Number(i.assets) || 0) - (Number(i.liabilities) || 0)), total_assets: round(Number(i.assets) || 0), total_liabilities: round(Number(i.liabilities) || 0) }),
        chartType: "none",
    },
    fire: {
        slug: "fire",
        name: "FIRE Calculator",
        category: "Planning",
        icon: "Rocket",
        tagline: "Financial Independence, Retire Early.",
        about: "Find out when you can stop working using the 4% rule (or your custom withdrawal rate).",
        formula: "FIRE Corpus = Annual Expense × (100 / Withdrawal Rate)",

        formulaExplanation: [
            "Based on FIRE withdrawal rule",
            "Assumes fixed annual expenses",
            "Uses projected investment growth",
            "Results are indicative only",
        ],
        defaults: { annualExpense: 600000, currentSavings: 500000, monthlySavings: 25000, rate: 12 },
        fields: [
            { key: "annualExpense", label: "Annual Expense", unit: "₹", min: 50000, max: 50000000, step: 10000 },
            { key: "currentSavings", label: "Current Savings", unit: "₹", min: 0, max: 100000000, step: 10000 },
            { key: "monthlySavings", label: "Monthly Savings", unit: "₹", min: 1000, max: 1000000, step: 1000 },
            { key: "rate", label: "Expected Return", unit: "%", min: 1, max: 25, step: 0.5 },
        ],
        run: calcFIRE,
        chartType: "line",
    },
    "real-estate-roi": {
        slug: "real-estate-roi",
        name: "Real Estate ROI",
        category: "Investments",
        icon: "Building2",
        tagline: "Property investment returns and CAGR.",
        about: "See total return, ROI and capital CAGR for any property.",
        defaults: { purchase: 5000000, sale: 8000000, years: 5, rental: 240000, expenses: 0 },
        fields: [
            { key: "purchase", label: "Purchase Price", unit: "₹", min: 100000, max: 1000000000, step: 100000 },
            { key: "sale", label: "Sale Price", unit: "₹", min: 100000, max: 1000000000, step: 100000 },
            { key: "years", label: "Holding Years", unit: "yrs", min: 1, max: 50, step: 1 },
            { key: "rental", label: "Annual Rental", unit: "₹", min: 0, max: 10000000, step: 10000 },
            { key: "expenses", label: "Total Expenses", unit: "₹", min: 0, max: 50000000, step: 10000 },
        ],
        run: calcRealEstateROI,
        chartType: "none",
    },
    "loan-eligibility": {
        slug: "loan-eligibility",
        name: "Loan Eligibility",
        category: "Loans",
        icon: "BadgeCheck",
        tagline: "Maximum loan amount you may get.",
        about: "Based on your income, existing EMIs and FOIR (default 50%).",
        formula: "Eligibility based on FOIR and EMI affordability",

        formulaExplanation: [
            "FOIR = Fixed Obligation to Income Ratio",
            "Loan eligibility derived from affordable EMI",
            "Uses reducing balance EMI model",
        ],
        defaults: { income: 80000, existingEMI: 0, rate: 9, tenure: 20, foir: 50 },
        fields: [
            { key: "income", label: "Monthly Income", unit: "₹", min: 10000, max: 5000000, step: 1000 },
            { key: "existingEMI", label: "Existing EMIs", unit: "₹", min: 0, max: 1000000, step: 500 },
            { key: "rate", label: "Interest Rate", unit: "%", min: 5, max: 20, step: 0.25 },
            { key: "tenure", label: "Tenure", unit: "yrs", min: 1, max: 30, step: 1 },
            { key: "foir", label: "FOIR", unit: "%", min: 20, max: 70, step: 5 },
        ],
        run: calcLoanEligibility,
        chartType: "none",
    },
    "debt-payoff": {
        slug: "debt-payoff",
        name: "Debt Payoff",
        category: "Loans",
        icon: "Scissors",
        tagline: "Time to clear a debt at a given payment.",
        about: "Avalanche-style debt payoff: see months to zero and total interest paid.",
        formula: "Reducing balance debt payoff projection",

        formulaExplanation: [
            "Interest added monthly",
            "Fixed monthly payment deducted",
            "Projection continues until balance reaches zero",
        ],
        defaults: { debt: 100000, rate: 18, monthlyPayment: 5000 },
        fields: [
            { key: "debt", label: "Debt Amount", unit: "₹", min: 1000, max: 100000000, step: 1000 },
            { key: "rate", label: "Interest Rate", unit: "%", min: 1, max: 50, step: 0.5 },
            { key: "monthlyPayment", label: "Monthly Payment", unit: "₹", min: 100, max: 500000, step: 500 },
        ],
        run: calcDebtPayoff,
        chartType: "none",
    },
    "asset-allocation": {
        slug: "asset-allocation",
        name: "Asset Allocation",
        category: "Planning",
        icon: "PieChart",
        tagline: "Suggested mix of equity, debt and gold.",
        about: "Based on your age and risk profile.",
        formula: "Age-based heuristic allocation model",

        formulaExplanation: [
            "Equity allocation derived from age",
            "Risk profile adjusts allocation",
            "Gold fixed at 10%",
            "Indicative allocation only",
        ],
        defaults: { age: 30, risk: "moderate" },
        fields: [{ key: "age", label: "Age", unit: "yrs", min: 18, max: 80, step: 1 }],
        select: { key: "risk", label: "Risk Profile", options: ["conservative", "moderate", "aggressive"] },
        run: calcAssetAllocation,
        chartType: "pie",
    },
};

export const CALCULATOR_LIST = Object.values(CALCULATORS);
